import { useMemo, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import {
  CHAT_WALLPAPER_IDS,
  CHAT_WALLPAPER_PRESETS,
  compressWallpaperImage,
  type ChatWallpaperId,
  type ChatWallpaperPrefs,
} from "@/lib/theme/chatWallpapers";

type Props = {
  prefs: ChatWallpaperPrefs;
  onChange: (next: ChatWallpaperPrefs) => void;
};

export function ChatWallpaperPicker({ prefs, onChange }: Props) {
  const opacityLabel = useMemo(() => `${prefs.opacity} %`, [prefs.opacity]);
  const blurLabel = useMemo(() => `${prefs.blur} %`, [prefs.blur]);
  const enabled = prefs.id !== "none";
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectPreset = (id: ChatWallpaperId) => {
    setError(null);
    if (id === "custom") {
      if (prefs.customUrl) {
        onChange({ ...prefs, id: "custom", customUrl: prefs.customUrl });
        return;
      }
      fileRef.current?.click();
      return;
    }
    onChange({ ...prefs, id, customUrl: null });
  };

  const onPickFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const customUrl = await compressWallpaperImage(file);
      onChange({ ...prefs, id: "custom", customUrl, opacity: prefs.opacity || 55 });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 text-xs font-medium text-[var(--chat-muted)]">Fond de discussion</p>
        <div
          className="grid grid-cols-3 gap-1.5"
          role="listbox"
          aria-label="Fond de discussion"
        >
          {CHAT_WALLPAPER_IDS.map((id) => {
            const preset = CHAT_WALLPAPER_PRESETS[id];
            const selected = prefs.id === id;
            const isCustom = id === "custom";
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={selected}
                title={
                  isCustom
                    ? prefs.customUrl
                      ? "Ma photo — recliquer pour en changer"
                      : "Importer une photo"
                    : preset.label
                }
                disabled={busy && isCustom}
                className={`group flex flex-col overflow-hidden rounded-xl border text-left transition disabled:opacity-60 ${
                  selected
                    ? "border-[var(--chat-accent)] ring-2 ring-[var(--chat-accent)]/30"
                    : "border-[var(--chat-border)] hover:border-[var(--chat-accent)]/40"
                }`}
                onClick={() => {
                  if (isCustom && prefs.customUrl && selected) {
                    fileRef.current?.click();
                    return;
                  }
                  selectPreset(id);
                }}
              >
                <span
                  className="chat-wallpaper-swatch relative block h-12 w-full"
                  data-wallpaper={id}
                  style={
                    id === "none"
                      ? { background: "var(--chat-bg)" }
                      : isCustom && prefs.customUrl
                        ? {
                            backgroundImage: `url(${prefs.customUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : isCustom
                          ? { background: "var(--chat-surface-elevated)" }
                          : { background: preset.preview, opacity: 0.85 }
                  }
                  aria-hidden
                >
                  {id === "none" ? (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-[var(--chat-muted)]">
                      —
                    </span>
                  ) : null}
                  {isCustom && !prefs.customUrl ? (
                    <span className="absolute inset-0 flex items-center justify-center text-[var(--chat-accent-strong)]">
                      <ImagePlus className="size-4" />
                    </span>
                  ) : null}
                </span>
                <span className="truncate bg-[var(--chat-surface)] px-1.5 py-1 text-[9px] leading-tight text-[var(--chat-muted)] group-hover:text-[var(--chat-text)]">
                  {isCustom ? (busy ? "Import…" : "Ma photo") : preset.label}
                </span>
              </button>
            );
          })}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            void onPickFile(f);
          }}
        />
        {error ? (
          <p className="mt-1.5 text-xs font-medium text-[var(--chat-danger)]" role="alert">
            {error}
          </p>
        ) : null}
        {prefs.id === "custom" && prefs.customUrl ? (
          <button
            type="button"
            className="mt-1.5 text-[10px] font-medium text-[var(--chat-accent-strong)] hover:underline"
            onClick={() => fileRef.current?.click()}
          >
            Changer la photo
          </button>
        ) : null}
      </div>

      <label className={`block space-y-1.5 ${enabled ? "" : "opacity-50"}`}>
        <span className="flex items-center justify-between text-xs text-[var(--chat-muted)]">
          <span>Opacité du fond</span>
          <span className="tabular-nums text-[var(--chat-text)]">{opacityLabel}</span>
        </span>
        <input
          type="range"
          min={10}
          max={100}
          step={1}
          disabled={!enabled}
          value={prefs.opacity}
          onChange={(e) =>
            onChange({ ...prefs, opacity: Number(e.target.value) || prefs.opacity })
          }
          className="chat-wallpaper-opacity w-full"
          aria-label="Opacité du fond"
        />
      </label>

      <label className={`block space-y-1.5 ${enabled ? "" : "opacity-50"}`}>
        <span className="flex items-center justify-between text-xs text-[var(--chat-muted)]">
          <span>Flou du fond</span>
          <span className="tabular-nums text-[var(--chat-text)]">{blurLabel}</span>
        </span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          disabled={!enabled}
          value={prefs.blur}
          onChange={(e) =>
            onChange({ ...prefs, blur: Number(e.target.value) || 0 })
          }
          className="chat-wallpaper-opacity w-full"
          aria-label="Flou du fond"
        />
      </label>
    </div>
  );
}
